import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router";
import { Formik, Form } from "formik";

import { Button } from "../../components";
import { ERoutes } from "../../constants/routes";
import AuthLayout from "../../layout/AuthLayout/AuthLayout";
import { invitationRegisterSchema } from "../../utils/validators";
import { fetchPaymentStatusSuccess } from "../../store/auth/slice";
import FormikInputField from "../../components/FormikField/FormikInputField/FormikInputField";
import { registerByInvitation, type InvitationAcceptInfo } from "../../api/pendingInvitations";
import { supabase } from "@/lib/supabase";
import { TERMS_URL, PRIVACY_URL } from "../../constants/config";

import styles from "./InvitationRegisterForm.module.css";

interface Props {
  token: string;
  invitation: InvitationAcceptInfo;
}

const InvitationRegisterForm: FC<Props> = ({ token, invitation }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: {
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    setLoading(true);
    setError(null);

    const result = await registerByInvitation(token, {
      email: invitation.email,
      password: values.password,
      first_name: values.firstName,
      last_name: values.lastName,
    });

    if ("error" in result) {
      setError(result.error);
      setLoading(false);
      return;
    }

    // Auto-login after successful registration
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: invitation.email,
      password: values.password,
    });

    setLoading(false);

    if (signInError) {
      setError(
        "Account created successfully, but auto-login failed. Please sign in manually."
      );
      return;
    }

    // Pre-set payment status in Redux BEFORE navigating to prevent payment redirect.
    // The new member has no Stripe subscription, but should inherit the owner's.
    dispatch(fetchPaymentStatusSuccess("paid"));

    navigate(ERoutes.Home, { replace: true });
  };

  return (
    <AuthLayout>
      <div className={styles.register}>
        <header className={styles.register__header}>
          <h1 className={styles.register__title}>Join the team</h1>
          <p className={styles.register__subtitle}>
            {invitation.inviter_email ? (
              <>
                <strong>{invitation.inviter_email}</strong> invited you to join{" "}
              </>
            ) : (
              <>You've been invited to join </>
            )}
            {invitation.account_name ? (
              <span className={styles.register__account_name}>
                {invitation.account_name}
              </span>
            ) : (
              "the team"
            )}
          </p>
        </header>

        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            password: "",
          }}
          validationSchema={invitationRegisterSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form className={styles.form}>
              <div className={styles.form__fields}>
                <div className={styles.form__field}>
                  <FormikInputField
                    placeholder="First name"
                    type="text"
                    name="firstName"
                    id="inv-firstName"
                  />
                </div>
                <div className={styles.form__field}>
                  <FormikInputField
                    placeholder="Last name"
                    type="text"
                    name="lastName"
                    id="inv-lastName"
                  />
                </div>
              </div>

              <div className={styles.form__field}>
                <FormikInputField
                  placeholder={invitation.email}
                  type="email"
                  name="email"
                  id="inv-email"
                  disabled
                />
              </div>

              <div className={styles.form__field}>
                <FormikInputField
                  placeholder="Password"
                  type="password"
                  name="password"
                  id="inv-password"
                />
              </div>

              <p className={styles.form__agreement}>
                By clicking this button, you agree to our{" "}
                <Link target="_blank" to={TERMS_URL}>
                  Terms of Service
                </Link>{" "}
                and
                <Link target="_blank" to={PRIVACY_URL}>
                  {" "}
                  Privacy policy
                </Link>
                .
              </p>

              <Button
                size="lg"
                type="submit"
                className={styles.form__submit}
                label={loading ? "Creating account..." : "Create account & join"}
                typeStyle="fill-auth"
                disabled={loading}
              />
              {error && <div className={styles.form__error}>{error}</div>}
            </Form>
          )}
        </Formik>

        <footer className={styles.footer}>
          <p className={styles.footer__caption}>
            Already have an AICARMA account?{" "}
            <Link to={ERoutes.Login}>Log in</Link>
          </p>
        </footer>
      </div>
    </AuthLayout>
  );
};

export default InvitationRegisterForm;
